import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fmtPrice } from "@/lib/api";
import { EASE } from "@/components/Reveal";

export default function CartDrawer() {
  const { items, setQty, remove, subtotal, drawerOpen, setDrawerOpen, checkout, checkingOut } = useCart();

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-ink/45 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed top-0 right-0 bottom-0 z-[90] w-full sm:max-w-md bg-linen border-l border-ink/10 flex flex-col"
          >
            <div className="p-6 sm:p-8 flex items-start justify-between border-b border-ink/10">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Notecard Collection</p>
                <h3 className="font-serif text-2xl sm:text-3xl text-ink mt-1">Your Portfolio Bag</h3>
              </div>
              <button data-testid="cart-drawer-close-btn" onClick={() => setDrawerOpen(false)} aria-label="Close cart"
                className="w-9 h-9 rounded-full border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-linen transition-colors">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 sm:p-8">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3" data-testid="cart-empty">
                  <p className="font-serif text-2xl text-ink">The bag is empty</p>
                  <p className="text-sm text-stone2 max-w-[26ch]">Signed original cards on 140 lb Arches are waiting on the table — each with envelope and protective sleeve.</p>
                </div>
              ) : (
                <ul className="space-y-6">
                  {items.map((i) => (
                    <li key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-4">
                      <img src={i.image} alt={i.title} className="w-20 h-20 object-cover rounded-sm border border-ink/10" />
                      <div className="flex-1">
                        <p className="font-serif text-lg leading-tight">{i.title}</p>
                        <p className="font-mono text-xs text-stone2 mt-1">{fmtPrice(i.price_cents)}</p>
                        <div className="mt-3 flex items-center gap-3">
                          <div className="inline-flex items-center border border-ink/15 rounded-full">
                            <button data-testid={`cart-qty-minus-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="w-8 h-8 flex items-center justify-center hover:text-terracotta" aria-label="Decrease">
                              <Minus size={13} />
                            </button>
                            <span className="font-mono text-xs w-6 text-center">{i.qty}</span>
                            <button data-testid={`cart-qty-plus-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="w-8 h-8 flex items-center justify-center hover:text-terracotta" aria-label="Increase">
                              <Plus size={13} />
                            </button>
                          </div>
                          <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} aria-label={`Remove ${i.title} from bag`}
                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/70 hover:text-destructive underline underline-offset-4 decoration-ink/30 hover:decoration-destructive transition-colors">
                            <Trash2 size={13} /> Remove
                          </button>
                        </div>
                      </div>
                      <p className="font-mono text-sm text-ink">{fmtPrice(i.price_cents * i.qty)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="p-6 sm:p-8 border-t border-ink/10 bg-paper">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage mb-4" data-testid="free-shipping-note">
                  Free mailing on every card — envelope &amp; protective sleeve included
                </p>
                <div className="flex justify-between items-baseline mb-5">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-stone2">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-serif text-2xl text-ink">{fmtPrice(subtotal)}</span>
                </div>
                <button data-testid="cart-checkout-stripe-btn" onClick={checkout} disabled={checkingOut}
                  className="w-full inline-flex items-center justify-center gap-2 bg-ink text-linen rounded-full py-4 text-xs font-mono uppercase tracking-[0.2em] hover:bg-terracotta transition-colors duration-300 disabled:opacity-60">
                  <Lock size={13} /> {checkingOut ? "Preparing secure checkout…" : "Secure Checkout"}
                </button>
                <p className="text-[11px] text-stone2 text-center mt-3">Taxes calculated at checkout. Cards ship free, flat-packed; framed pieces ship insured.</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
